import React from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, BookOpen, ShieldCheck, Mail, ArrowRight, Award, Target, Heart } from 'lucide-react';

const VALEURS = [
  {
    icon: ShieldCheck,
    title: 'Pédagogie ECSR',
    desc: 'Tous les contenus sont rédigés et vérifiés par des moniteurs titulaires du titre ECSR (Enseignant de la Conduite et de la Sécurité Routière). Aucun copywriter, aucun rédacteur SEO : que des pros du terrain.',
  },
  {
    icon: Heart,
    title: 'Gratuit · sans pub · sans tracking',
    desc: 'Pas de mur de paiement, pas de bandeau publicitaire, pas de cookie tiers. Le code est notre métier — pas un produit d\'appel pour vendre autre chose.',
  },
  {
    icon: BookOpen,
    title: 'Format magazine, pas QCM',
    desc: 'On ne croit pas aux 4000 questions répétitives. On croit à la compréhension : enquêtes terrain, dossiers thématiques, données ONISR mises à jour mensuellement.',
  },
  {
    icon: Award,
    title: 'Conforme au REMC 2024',
    desc: 'Tous les modules suivent le Référentiel pour l\'Éducation à une Mobilité Citoyenne (4 compétences, 38 sous-savoirs). Outil compagnon du livret d\'apprentissage officiel.',
  },
];

const CHIFFRES = [
  { v: '26', l: 'Articles magazine' },
  { v: '38', l: 'Sous-savoirs REMC' },
  { v: '9', l: 'Thèmes ETG' },
  { v: '100 %', l: 'Gratuit' },
];

export default function AProposPage() {
  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12">
        <SectionTitle
          kicker="À propos · Code Voiture"
          title="Le code voiture, pensé comme un magazine."
          sub="Code Voiture est une plateforme pédagogique gratuite conçue par des moniteurs ECSR. Notre conviction : la sécurité routière ne se mémorise pas par cœur, elle se comprend."
        />

        {/* Mission */}
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-8 lg:p-10">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3 flex items-center gap-2"><Target size={13} /> Notre mission</div>
            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[44px] leading-tight">Former des conducteurs lucides, pas seulement diplômés.</h2>
            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4 text-white/75 text-[13.5px] sm:text-[15px] leading-relaxed">
              <p>
                En France, <strong className="text-white">3 200 personnes</strong> meurent chaque année sur la route et près de 16 000 sont blessées gravement (source ONISR 2025). La majorité des accidents impliquent une <em>erreur d'analyse</em>, pas un défaut technique du conducteur.
              </p>
              <p>
                Nous pensons qu'on ne change pas durablement un comportement avec un QCM noté. On le change en comprenant <strong className="text-white">pourquoi</strong> une distance de sécurité, <strong className="text-white">pourquoi</strong> on perd 1 seconde de réaction à 0,5 g/L d'alcool, <strong className="text-white">pourquoi</strong> un angle mort tue un cycliste à l'intersection.
              </p>
              <p>
                Code Voiture est notre réponse : des contenus longs, sourcés, illustrés, mis à jour avec les statistiques officielles ONISR — accessibles à tous, sans inscription obligatoire.
              </p>
            </div>
          </div>

          <div className="rounded-2xl sm:rounded-3xl border border-red-900/40 bg-gradient-to-br from-red-950/30 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-8 lg:p-10">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-4 sm:mb-5">Chiffres clés</div>
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {CHIFFRES.map((c, i) => (
                <div key={i}>
                  <div className="font-serif-editorial text-white text-[32px] sm:text-[44px] leading-none">{c.v}</div>
                  <div className="text-white/55 text-[11px] sm:text-[12px] mt-1 font-mono-spec uppercase tracking-wider">{c.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-white/10">
              <div className="text-white/60 text-[12px] sm:text-[12.5px]">Lancée en 2025 par un collectif de moniteurs ECSR de la région PACA, la plateforme s'enrichit chaque mois de nouveaux contenus et d'outils interactifs.</div>
            </div>
          </div>
        </div>

        {/* Valeurs */}
        <div className="mb-12 sm:mb-16">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3">Nos engagements</div>
          <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[44px] leading-tight mb-6 sm:mb-10">4 principes non négociables</h2>
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
            {VALEURS.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6 lg:p-7 hover:border-red-700/40 transition">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 mb-3 sm:mb-4"><Icon size={18} /></div>
                  <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight">{v.title}</h3>
                  <p className="text-white/65 text-[12.5px] sm:text-[13.5px] mt-2 sm:mt-3 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Deux audiences */}
        <div className="mb-12 sm:mb-16">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3">Pour qui ?</div>
          <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[44px] leading-tight mb-6 sm:mb-10">Deux publics, une même rigueur</h2>
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
            <Link to="/eleve" className="group rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 lg:p-8 hover:border-red-700/40 transition">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><GraduationCap size={20} /></div>
                <ArrowRight size={18} className="text-white/30 group-hover:text-red-400 group-hover:translate-x-1 transition" />
              </div>
              <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] mt-4 sm:mt-5">Espace Élève</h3>
              <p className="text-white/65 text-[12.5px] sm:text-[13.5px] mt-2 sm:mt-3">Permis B, AAC, Conduite Supervisée, BEA, B96. Magazine, tutos vidéo, simulateurs, examens blancs et livret d'apprentissage numérique conforme au REMC.</p>
            </Link>
            <Link to="/enseignant" className="group rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 lg:p-8 hover:border-red-700/40 transition">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><Users size={20} /></div>
                <ArrowRight size={18} className="text-white/30 group-hover:text-red-400 group-hover:translate-x-1 transition" />
              </div>
              <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] mt-4 sm:mt-5">Espace Enseignant ECSR</h3>
              <p className="text-white/65 text-[12.5px] sm:text-[13.5px] mt-2 sm:mt-3">Mode présentation pour cours en salle, grilles d'évaluation 31 points, suivi élèves, baromètre ONISR mensuel et bibliothèque de cours interactifs prêts à l'emploi.</p>
            </Link>
          </div>
        </div>

        {/* Contact */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0a0a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-8 lg:p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-red-600/15 border border-red-700/30 text-red-400 mb-4 sm:mb-5"><Mail size={20} /></div>
          <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] lg:text-[38px] leading-tight">Une question, une suggestion, un signalement ?</h3>
          <p className="text-white/65 text-[13px] sm:text-[14.5px] mt-3 max-w-xl mx-auto">Vous êtes moniteur, candidat, ou simplement curieux ? Écrivez-nous : nous répondons à chaque message.</p>
          <a data-testid="apropos-contact-btn" href="mailto:contact@codevoiture.fr?subject=Contact%20codevoiture.fr" className="mt-6 sm:mt-7 inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold shadow-lg shadow-red-900/40">
            <Mail size={15} /> contact@codevoiture.fr
          </a>
        </div>
      </section>
    </PageShell>
  );
}
